import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Code,
  Shield,
  FileText,
  Terminal,
  CheckCircle,
  XCircle,
  Copy,
  ExternalLink,
  Home,
  Book,
  Server,
  Database,
  Lock,
  Eye,
  EyeOff,
  AlertTriangle,
  Scissors,
  RefreshCw
} from "lucide-react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const SUPER_ADMIN_EMAIL = "georgebroughton71@gmail.com";

const DeveloperPortal = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [devModeStatus, setDevModeStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const isSuperAdmin = user?.email?.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase();

  useEffect(() => {
    if (!isSuperAdmin) {
      toast.error("Super Admin access required");
      navigate("/");
      return;
    }
    fetchDevModeStatus();
  }, [isSuperAdmin, navigate]);

  const fetchDevModeStatus = async () => {
    try {
      const response = await axios.get(`${API}/dev-mode`);
      setDevModeStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch dev mode status:", error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900">
      {/* Header */}
      <header className="bg-black/50 border-b border-cyan-500/20 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-white">
              <Home className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-lg flex items-center justify-center">
                <Code className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Developer Portal</h1>
                <p className="text-xs text-gray-500">MyHairChair Documentation & Tools</p>
              </div>
            </div>
          </div>
          <Badge className="bg-red-500/20 text-red-400 border border-red-500/30">
            <Shield className="w-3 h-3 mr-1" />
            Super Admin Only
          </Badge>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Dev Mode Status Banner */}
        <Card className={`mb-8 ${devModeStatus?.dev_mode 
          ? "bg-green-500/10 border-green-500/30" 
          : "bg-amber-500/10 border-amber-500/30"}`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {devModeStatus?.dev_mode ? (
                  <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                    <EyeOff className="w-6 h-6 text-green-400" />
                  </div>
                ) : (
                  <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                    <Eye className="w-6 h-6 text-amber-400" />
                  </div>
                )}
                <div>
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    Developer Mode: 
                    {devModeStatus?.dev_mode ? (
                      <Badge className="bg-green-500 text-white">ENABLED</Badge>
                    ) : (
                      <Badge className="bg-amber-500 text-black">DISABLED</Badge>
                    )}
                  </h2>
                  <p className="text-gray-400 text-sm mt-1">
                    {devModeStatus?.dev_mode 
                      ? "Client PII is being masked in API responses" 
                      : "Real client data is visible - Set DEV_MODE=true for development"}
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={fetchDevModeStatus}
                className="border-gray-600 text-gray-300"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Status
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Main Content Tabs */}
        <Tabs defaultValue="quickstart" className="space-y-6">
          <TabsList className="bg-gray-800/50 border border-gray-700 p-1">
            <TabsTrigger value="quickstart" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-white">
              <Terminal className="w-4 h-4 mr-2" />
              Quick Start
            </TabsTrigger>
            <TabsTrigger value="guide" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-white">
              <Book className="w-4 h-4 mr-2" />
              Developer Guide
            </TabsTrigger>
            <TabsTrigger value="api" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-white">
              <Server className="w-4 h-4 mr-2" />
              API Reference
            </TabsTrigger>
            <TabsTrigger value="masking" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-white">
              <Lock className="w-4 h-4 mr-2" />
              Data Masking
            </TabsTrigger>
          </TabsList>

          {/* Quick Start Tab */}
          <TabsContent value="quickstart">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Environment Setup */}
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Terminal className="w-5 h-5 text-cyan-400" />
                    Environment Setup
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-gray-400 text-sm mb-2">1. Run the setup script:</p>
                    <div className="bg-black/50 rounded-lg p-3 flex items-center justify-between">
                      <code className="text-green-400 text-sm">./scripts/dev-setup.sh</code>
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard("./scripts/dev-setup.sh")}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-2">2. Or manually set up:</p>
                    <div className="bg-black/50 rounded-lg p-3 space-y-1">
                      <code className="text-cyan-400 text-sm block">cd backend && pip install -r requirements.txt</code>
                      <code className="text-cyan-400 text-sm block">cd frontend && yarn install</code>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Required Environment Variables */}
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Database className="w-5 h-5 text-cyan-400" />
                    Backend .env (Required)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-black/50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between items-center">
                      <code className="text-amber-400 text-sm">DEV_MODE=true</code>
                      <Badge className="bg-red-500/20 text-red-400 text-xs">CRITICAL</Badge>
                    </div>
                    <code className="text-gray-400 text-sm block">MONGO_URL=mongodb://localhost:27017</code>
                    <code className="text-gray-400 text-sm block">DB_NAME=myhairchair_dev</code>
                    <code className="text-gray-400 text-sm block">JWT_SECRET=your_secret_key</code>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="mt-4 w-full border-gray-600 text-gray-300"
                    onClick={() => copyToClipboard(`DEV_MODE=true
MONGO_URL=mongodb://localhost:27017
DB_NAME=myhairchair_dev
JWT_SECRET=dev_secret_key`)}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy .env Template
                  </Button>
                </CardContent>
              </Card>

              {/* Start Commands */}
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Server className="w-5 h-5 text-cyan-400" />
                    Start Development Servers
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-gray-400 text-sm mb-2">Backend (Terminal 1):</p>
                    <div className="bg-black/50 rounded-lg p-3">
                      <code className="text-green-400 text-sm">uvicorn server:app --host 0.0.0.0 --port 8001 --reload</code>
                    </div>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-2">Frontend (Terminal 2):</p>
                    <div className="bg-black/50 rounded-lg p-3">
                      <code className="text-green-400 text-sm">yarn start</code>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Verify Setup */}
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-cyan-400" />
                    Verify Setup
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-gray-400 text-sm mb-2">Check dev mode is active:</p>
                    <div className="bg-black/50 rounded-lg p-3">
                      <code className="text-yellow-400 text-sm">curl http://localhost:8001/api/dev-mode</code>
                    </div>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                    <p className="text-green-400 text-sm">
                      ✓ Response should show: <code className="bg-black/30 px-1 rounded">"dev_mode": true</code>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Developer Guide Tab */}
          <TabsContent value="guide">
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Book className="w-5 h-5 text-cyan-400" />
                  Developer Guide
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-invert max-w-none">
                <div className="space-y-6">
                  {/* Tech Stack */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Tech Stack</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[
                        { name: "React 18", desc: "Frontend", color: "bg-blue-500/20 text-blue-400" },
                        { name: "FastAPI", desc: "Backend", color: "bg-green-500/20 text-green-400" },
                        { name: "MongoDB", desc: "Database", color: "bg-emerald-500/20 text-emerald-400" },
                        { name: "Stripe", desc: "Payments", color: "bg-purple-500/20 text-purple-400" },
                      ].map((tech, idx) => (
                        <div key={idx} className={`${tech.color} rounded-lg p-3 text-center`}>
                          <p className="font-semibold">{tech.name}</p>
                          <p className="text-xs opacity-70">{tech.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Project Structure */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Project Structure</h3>
                    <div className="bg-black/50 rounded-lg p-4 font-mono text-sm">
                      <pre className="text-gray-300">{`/app
├── backend/
│   ├── server.py          # Main FastAPI app
│   ├── utils/
│   │   └── data_masking.py  # PII masking
│   └── .env               # Environment vars
├── frontend/
│   ├── src/
│   │   ├── App.js         # Main React app
│   │   ├── pages/         # Route pages
│   │   └── components/    # UI components
│   └── .env
└── docs/
    ├── DEVELOPER_GUIDE.md
    └── API_REFERENCE.md`}</pre>
                    </div>
                  </div>

                  {/* Key Files */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Key Files</h3>
                    <div className="space-y-2">
                      {[
                        { file: "/app/docs/DEVELOPER_GUIDE.md", desc: "Full developer documentation" },
                        { file: "/app/docs/API_REFERENCE.md", desc: "API endpoints reference" },
                        { file: "/app/backend/server.py", desc: "Main backend application" },
                        { file: "/app/frontend/src/App.js", desc: "Main frontend with auth" },
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-black/30 rounded-lg p-3">
                          <code className="text-cyan-400 text-sm">{item.file}</code>
                          <span className="text-gray-500 text-sm">{item.desc}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* API Reference Tab */}
          <TabsContent value="api">
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Server className="w-5 h-5 text-cyan-400" />
                  API Reference
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Auth Endpoints */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <Lock className="w-4 h-4 text-amber-400" />
                      Authentication
                    </h3>
                    <div className="space-y-2">
                      {[
                        { method: "POST", path: "/api/auth/login", desc: "Login with email/password" },
                        { method: "POST", path: "/api/auth/register", desc: "Register new user" },
                        { method: "GET", path: "/api/auth/me", desc: "Get current user (auth required)" },
                        { method: "GET", path: "/api/auth/google/login", desc: "Initiate Google OAuth" },
                      ].map((endpoint, idx) => (
                        <div key={idx} className="flex items-center gap-3 bg-black/30 rounded-lg p-3">
                          <Badge className={endpoint.method === "GET" ? "bg-green-500" : "bg-blue-500"}>
                            {endpoint.method}
                          </Badge>
                          <code className="text-cyan-400 text-sm flex-1">{endpoint.path}</code>
                          <span className="text-gray-500 text-sm">{endpoint.desc}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Stylists Endpoints */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <Scissors className="w-4 h-4 text-amber-400" />
                      Stylists
                    </h3>
                    <div className="space-y-2">
                      {[
                        { method: "GET", path: "/api/stylists/", desc: "List all stylists (public)" },
                        { method: "GET", path: "/api/stylists/{id}", desc: "Get stylist details" },
                        { method: "PUT", path: "/api/stylists/profile", desc: "Update profile (auth)" },
                      ].map((endpoint, idx) => (
                        <div key={idx} className="flex items-center gap-3 bg-black/30 rounded-lg p-3">
                          <Badge className={endpoint.method === "GET" ? "bg-green-500" : "bg-amber-500"}>
                            {endpoint.method}
                          </Badge>
                          <code className="text-cyan-400 text-sm flex-1">{endpoint.path}</code>
                          <span className="text-gray-500 text-sm">{endpoint.desc}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Utility Endpoints */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <Terminal className="w-4 h-4 text-amber-400" />
                      Utility
                    </h3>
                    <div className="space-y-2">
                      {[
                        { method: "GET", path: "/api/health", desc: "Health check" },
                        { method: "GET", path: "/api/dev-mode", desc: "Check dev mode status" },
                      ].map((endpoint, idx) => (
                        <div key={idx} className="flex items-center gap-3 bg-black/30 rounded-lg p-3">
                          <Badge className="bg-green-500">{endpoint.method}</Badge>
                          <code className="text-cyan-400 text-sm flex-1">{endpoint.path}</code>
                          <span className="text-gray-500 text-sm">{endpoint.desc}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                    <p className="text-cyan-400 text-sm">
                      📄 Full API documentation available at: <code className="bg-black/30 px-2 py-1 rounded">/app/docs/API_REFERENCE.md</code>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Data Masking Tab */}
          <TabsContent value="masking">
            <div className="grid md:grid-cols-2 gap-6">
              {/* How It Works */}
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Shield className="w-5 h-5 text-cyan-400" />
                    How Data Masking Works
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-400 text-sm">
                    When <code className="bg-black/30 px-1 rounded text-amber-400">DEV_MODE=true</code> is set in the backend environment, 
                    all API responses containing client PII are automatically masked.
                  </p>
                  
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
                      <div>
                        <p className="text-amber-400 font-semibold text-sm">Important for Partner Developers</p>
                        <p className="text-gray-400 text-sm mt-1">
                          Always ensure DEV_MODE=true when working on the codebase to protect real client data.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Masking Examples */}
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Eye className="w-5 h-5 text-cyan-400" />
                    Masking Examples
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[
                      { field: "Name", original: "John Doe", masked: "J*** D***" },
                      { field: "Email", original: "john@gmail.com", masked: "j***@g***.c***" },
                      { field: "Phone", original: "+12345678901", masked: "+1***8901" },
                      { field: "Address", original: "123 Main St", masked: "*** M*** S***" },
                    ].map((item, idx) => (
                      <div key={idx} className="grid grid-cols-3 gap-2 text-sm">
                        <span className="text-gray-500">{item.field}</span>
                        <code className="text-red-400 bg-red-500/10 px-2 py-1 rounded">{item.original}</code>
                        <code className="text-green-400 bg-green-500/10 px-2 py-1 rounded">{item.masked}</code>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Affected Endpoints */}
              <Card className="bg-gray-800/50 border-gray-700 md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Server className="w-5 h-5 text-cyan-400" />
                    Affected Endpoints
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-4">
                    {[
                      { path: "GET /api/admin/users", fields: "name, email" },
                      { path: "GET /api/stylists/", fields: "user.name, user.email" },
                      { path: "GET /api/appointments/my", fields: "client.name, client.email, client.phone" },
                    ].map((endpoint, idx) => (
                      <div key={idx} className="bg-black/30 rounded-lg p-4">
                        <code className="text-cyan-400 text-sm block mb-2">{endpoint.path}</code>
                        <p className="text-gray-500 text-xs">Masked: {endpoint.fields}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800 mt-12 py-6 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <p className="text-gray-500 text-sm">MyHairChair Developer Portal • Super Admin Only</p>
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-gray-400 hover:text-white text-sm">
              Admin Dashboard
            </Link>
            <Link to="/" className="text-gray-400 hover:text-white text-sm">
              Back to App
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DeveloperPortal;
